/**
 * BulkImportModal Component
 * Allows users to upload Excel files for mass appointment import
 */
import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle, Loader2 } from "lucide-react";

import { API } from '../../config/api';

const BulkImportModal = ({ teams, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error("Formato file non supportato. Utilizzare file Excel (.xlsx o .xls)");
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File troppo grande. Dimensione massima: 10MB");
        return;
      }
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error("Formato file non supportato. Utilizzare file Excel (.xlsx o .xls)");
        return;
      }
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/bulk-import/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_import_appuntamenti.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Template scaricato con successo");
    } catch (error) {
      toast.error("Errore nel download del template");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Seleziona un file da caricare");
      return;
    }

    if (!selectedTeam) {
      toast.error("Seleziona un team per gli appuntamenti");
      return;
    }

    setIsUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(
        `${API}/bulk-import/appointments?team_id=${selectedTeam}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setResult(response.data);

      if (response.data.success) {
        toast.success(`Importati ${response.data.imported} appuntamenti con successo!`);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.warning(`Importazione completata con ${response.data.errors} errori`);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Errore durante l'importazione";
      toast.error(message);
      setResult({
        success: false,
        total_rows: 0,
        imported: 0,
        errors: 1,
        error_details: [{ row: 0, field: "file", message }]
      });
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-lojack-red rounded-lg">
              <FileSpreadsheet className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Importazione Massiva</h2>
              <p className="text-sm text-gray-500">Carica appuntamenti da file Excel</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-200 rounded-lg transition"
            data-testid="bulk-import-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Team selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Team di destinazione *
            </label>
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red focus:border-transparent"
              data-testid="bulk-import-team"
            >
              <option value="">Seleziona un team...</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>

          {/* Template download */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <Download className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-sm text-blue-800 font-medium">Scarica il template Excel</p>
                <p className="text-xs text-blue-600 mt-1">
                  Utilizza il template per formattare correttamente i dati da importare.
                  Include tutte le colonne richieste e un esempio.
                </p>
                <button
                  onClick={downloadTemplate}
                  className="mt-2 text-sm text-blue-700 hover:text-blue-900 font-medium underline"
                  data-testid="download-template"
                >
                  Scarica template_import_appuntamenti.xlsx
                </button>
              </div>
            </div>
          </div>

          {/* File upload area */}
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition ${
              selectedFile 
                ? 'border-green-400 bg-green-50' 
                : 'border-gray-300 hover:border-lojack-red hover:bg-red-50'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
            style={{ cursor: 'pointer' }}
            data-testid="upload-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            
            {selectedFile ? (
              <div className="flex flex-col items-center">
                <FileSpreadsheet className="text-green-600 mb-3" size={48} />
                <p className="font-medium text-gray-800">{selectedFile.name}</p>
                <p className="text-sm text-gray-500 mt-1">{formatFileSize(selectedFile.size)}</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setResult(null);
                  }}
                  className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
                >
                  Rimuovi file
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="text-gray-400 mb-3" size={48} />
                <p className="font-medium text-gray-700">
                  Trascina qui il file Excel o clicca per selezionare
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Formati supportati: .xlsx, .xls (max 10MB)
                </p>
              </div>
            )}
          </div>

          {/* Results */}
          {result && (
            <div className={`mt-6 p-4 rounded-lg ${
              result.success ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
            }`}>
              <div className="flex items-start gap-3">
                {result.success ? (
                  <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
                ) : (
                  <AlertCircle className="text-yellow-600 flex-shrink-0" size={24} />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${result.success ? 'text-green-800' : 'text-yellow-800'}`}>
                    {result.success ? 'Importazione completata!' : 'Importazione completata con errori'}
                  </p>
                  <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Righe totali:</span>
                      <span className="ml-2 font-medium">{result.total_rows}</span>
                    </div>
                    <div>
                      <span className="text-green-600">Importati:</span>
                      <span className="ml-2 font-medium text-green-700">{result.imported}</span>
                    </div>
                    <div>
                      <span className="text-red-600">Errori:</span>
                      <span className="ml-2 font-medium text-red-700">{result.errors}</span>
                    </div>
                  </div>
                  
                  {/* Download error file button */}
                  {result.error_file_base64 && (
                    <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="text-red-600" size={20} />
                          <span className="text-sm text-red-800 font-medium">
                            Scarica file Excel con errori evidenziati
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            // Decode base64 and download
                            const byteCharacters = atob(result.error_file_base64);
                            const byteNumbers = new Array(byteCharacters.length);
                            for (let i = 0; i < byteCharacters.length; i++) {
                              byteNumbers[i] = byteCharacters.charCodeAt(i);
                            }
                            const byteArray = new Uint8Array(byteNumbers);
                            const blob = new Blob([byteArray], { 
                              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                            });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', 'import_errori.xlsx');
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                            toast.success("File errori scaricato");
                          }}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition"
                          data-testid="download-error-file"
                        >
                          <Download size={16} />
                          Scarica
                        </button>
                      </div>
                      <p className="text-xs text-red-600 mt-2">
                        Il file contiene le righe originali con una colonna "ERRORI" che indica i problemi da correggere.
                      </p>
                    </div>
                  )}
                  
                  {/* Error details table */}
                  {result.error_details && result.error_details.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Dettagli errori ({result.error_details.length} totali):
                      </p>
                      <div className="max-h-48 overflow-y-auto bg-white rounded border">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100 sticky top-0">
                            <tr>
                              <th className="px-2 py-1 text-left w-16">Riga</th>
                              <th className="px-2 py-1 text-left w-24">Campo</th>
                              <th className="px-2 py-1 text-left">Errore</th>
                              <th className="px-2 py-1 text-left w-32">Valore</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.error_details.map((err, idx) => (
                              <tr key={idx} className="border-t hover:bg-red-50">
                                <td className="px-2 py-1 font-mono text-red-600">{err.row}</td>
                                <td className="px-2 py-1 font-medium text-gray-700">{err.field}</td>
                                <td className="px-2 py-1 text-red-600">{err.message}</td>
                                <td className="px-2 py-1 text-gray-500 truncate max-w-[120px]" title={err.original_value || '-'}>
                                  {err.original_value || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-2">Colonne obbligatorie:</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
              <span>• Cliente (client_name)</span>
              <span>• Indirizzo (address)</span>
              <span>• Città (city)</span>
              <span>• Provincia (province)</span>
              <span>• Data Appuntamento (scheduled_date)</span>
              <span>• Ora Appuntamento (scheduled_time)</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
            data-testid="bulk-import-cancel"
          >
            {result ? 'Chiudi' : 'Annulla'}
          </button>
          {!result?.success && (
            <button
              onClick={handleUpload}
              disabled={!selectedFile || !selectedTeam || isUploading}
              className="flex items-center gap-2 px-6 py-2 bg-lojack-red text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="bulk-import-submit"
            >
              {isUploading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Importazione...
                </>
              ) : (
                <>
                  <Upload size={18} />
                  Importa Appuntamenti
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;
